/**
 * @file useComicInteract.js
 * @description 漫画阅读页 — 互动区（点赞/收藏/评论）
 * @module features/comic/composables/useComicInteract
 */

import { fetchVolInteractApi } from '../api/comicApi'
import {
  addMessageApi,
  cancelMessageLikeApi,
  deleteMessageApi,
  fetchMessageReplyPageApi,
  insertMessageLikeApi,
  replyMessageApi,
} from '@/features/message/api/messageApi'
import {
  cancelVolCollectApi,
  fetchUserBaseApi,
  insertVolCollectApi,
} from '@/features/user/api/userApi'
import { convertRouteParams } from '@/shared/utils/idHelper'
import { normalizeVolInteractData, isVolCommentLiked, mergeVolInteractComments, VOL_INTERACT_LOAD_MORE_PAGE_SIZE, VOL_INTERACT_COLLAPSED_COMMENT_LIMIT } from '../utils/normalizeVolInteract'
import { flashHighlightElement } from '@/features/message/utils/commentHighlight'
import {
  ensureCommentMessageId,
  normalizeMessageFields,
  resolveAddMessageId,
} from '@/features/message/utils/normalizeMessageFields'
import {
  isDefaultAvatarSrc,
  PLACEHOLDER_ASSETS,
} from '@/shared/assets/manifest'
import { openComicTip } from './useComicTip'

/**
 * @param {Record<string, Function>} bridge 共享方法桥
 * @param {object} ctx reactive 状态
 */
export function useComicInteract(bridge, ctx) {
  function readerCommentElId(comment) {
    if (!comment) return undefined
    const raw = comment.messageId ?? comment.id
    if (raw === undefined || raw === null || String(raw).trim() === '') return undefined
    return `reader-comment-${String(raw).trim()}`
  }

  function tryScrollToHighlightedVolComment() {
    const hid = ctx.$route.query.highlightComment
    if (hid === undefined || hid === null || String(hid).trim() === '') return
    const target = String(hid).trim()
    ctx.$nextTick(() => {
      setTimeout(async () => {
        const el = document.getElementById(`reader-comment-${target}`)
        if (el) {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' })
          setTimeout(() => flashHighlightElement(el), 520)
          const q = { ...ctx.$route.query }
          delete q.highlightComment
          await ctx.$router.replace({ query: q })
          return
        }
        await ctx.$router.replace({
          name: 'ComicDetail',
          params: convertRouteParams({ id: ctx.comicId }),
          query: { highlightComment: target }
        })
      }, 450)
    })
  }

  async function fetchInteractData() {

    if (!ctx.volId) {
      console.warn('跳过获取互动数据：volId 为空')
      return
    }

    if (ctx.isLoadingInteract) {
      console.warn('跳过获取互动数据：正在加载中')
      return
    }

    try {
            ctx.isLoadingInteract = true

      const response = await fetchVolInteractApi(ctx.volId, ctx.token)

      if (response.data.code === 200 || response.data.code === 0) {
        ctx.interactData = normalizeVolInteractData(response.data.data || null)
      } else {
        console.error('获取互动数据失败:', response.data)
      }
    } catch (error) {
      console.error('请求互动数据失败:', error)
      console.error('错误详情:', error.response || error.message)
    } finally {
      ctx.isLoadingInteract = false
            // 互动数据加载完成后，检查当前卷是否完成
      checkCurrentVolumeComplete()
      tryScrollToHighlightedVolComment()
    }
  }

  function checkCurrentVolumeComplete() {
    const imagesComplete = !ctx.hasNext && ctx.pages.length > 0
    const interactComplete = ctx.interactData !== null || (!ctx.token && !ctx.hasNext) // 未登录用户如果没有下一页也算完成

    ctx.isCurrentVolumeComplete = imagesComplete && interactComplete

    // 如果卷已完成，重置滚动触发标志，允许用户向上滑动加载下一卷
    if (ctx.isCurrentVolumeComplete) {
      ctx.scrollTriggeredLoadNext = false
    }
  }

  /**
   * 规范化头像 URL（支持相对路径与绝对 URL）
   * @param {string} url
   * @returns {string}
   */
  function resolveAvatarUrl(url) {
    if (!url) return ''
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url
    }
    return url.startsWith('/') ? url : `/${url}`
  }

  /**
   * 从用户信息对象中提取头像字段
   * @param {{ headUrl?: string, avatar?: string } | null | undefined} user
   * @returns {string}
   */
  function getUserAvatarFromProfile(user) {
    return user?.headUrl || user?.avatar || ''
  }

  async function fetchCurrentUser() {
    if (!ctx.token) {
      ctx.currentUser = null
      return
    }

    try {
      const response = await fetchUserBaseApi(ctx.token)
      if (response.data.code === 200 || response.data.code === 0) {
        const vo = response.data.baseInfoVo || null
        if (vo) {
          ctx.currentUser = {
            ...vo,
            avatar: vo.headUrl || vo.avatar || '',
          }
        } else {
          ctx.currentUser = null
        }
      }
    } catch (error) {
      console.error('获取用户信息失败:', error)
      ctx.currentUser = null
    }
  }

  async function handleLike() {
    if (!ctx.token) {
      ctx.$emit('show-popup-message', '请您登录后操作', 'login-required')
      return
    }

    if (!ctx.interactData || !ctx.volId) {
      return
    }

    try {
      const isLike = ctx.interactData.isLike
      const payload = {
        targetId: ctx.volId,
        type: 'VOL',
      }
      const response = isLike
        ? await cancelMessageLikeApi(payload, ctx.token)
        : await insertMessageLikeApi(payload, ctx.token)

      if (response.data.code === 200 || response.data.code === 0) {
        // 更新本地状态
        ctx.interactData.isLike = !isLike
        ctx.interactData.likeCount = isLike 
          ? (ctx.interactData.likeCount - 1) 
          : (ctx.interactData.likeCount + 1)
      } else {
        ctx.$emit('show-popup-message', response.data.msg || '操作失败', 'error')
      }
    } catch (error) {
      console.error('点赞操作失败:', error)
      ctx.$emit('show-popup-message', '操作失败', 'error')
    }
  }

  async function handleCollect() {
    if (!ctx.token) {
      ctx.$emit('show-popup-message', '请您登录后操作', 'login-required')
      return
    }

    if (!ctx.interactData || !ctx.volId) {
      return
    }

    try {
      const isCollect = ctx.interactData.isCollect
      const collectPayload = {
        comicId: ctx.comicId,
        volNum: Number(ctx.volNum),
      }
      const response = isCollect
        ? await cancelVolCollectApi(collectPayload, ctx.token)
        : await insertVolCollectApi(collectPayload, ctx.token)

      if (response.data.code === 200 || response.data.code === 0) {
        // 更新本地状态
        ctx.interactData.isCollect = !isCollect
      } else {
        ctx.$emit('show-popup-message', response.data.msg || '操作失败', 'error')
      }
    } catch (error) {
      console.error('收藏操作失败:', error)
      ctx.$emit('show-popup-message', '操作失败', 'error')
    }
  }

  function formatLocalCommentTime() {
    return new Date()
      .toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        second: '2-digit',
      })
      .replace(/\//g, '-')
  }

  /**
   * 根据发评接口响应构建可点赞的本地评论对象
   * @param {object} responseData
   * @param {string} commentContent
   * @returns {object | null}
   */
  function buildSubmittedVolComment(responseData, commentContent) {
    const contentVo = responseData?.content
    if (contentVo && typeof contentVo === 'object') {
      return ensureCommentMessageId(
        normalizeMessageFields({
          ...contentVo,
          content: contentVo.content || commentContent,
        }),
      )
    }

    const messageId = resolveAddMessageId(responseData)
    if (!messageId) return null

    return ensureCommentMessageId(
      normalizeMessageFields({
        messageId,
        userId: ctx.currentUser?.id || '',
        userName: ctx.currentUser?.name || '我',
        userHeadUrl: getUserAvatarFromProfile(ctx.currentUser) || getAvatarUrl(),
        content: commentContent,
        commentTime: formatLocalCommentTime(),
        likeCount: 0,
        replyCount: 0,
        isMyLike: false,
        isBelongMe: true,
      }),
    )
  }

  function appendCommentToExtractVo(extractVo, newComment) {
    if (!extractVo.commentVos) {
      extractVo.commentVos = []
    }
    extractVo.commentVos.unshift(newComment)
    extractVo.contentTotal = (extractVo.contentTotal || 0) + 1
    extractVo.totalCount = Number(extractVo.totalCount ?? 0) + 1
  }

  /**
   * 卷互动区 contentTotal +1（含评论回复）
   * @param {{ interactData?: object } | null} [volHolder]
   */
  function incrementVolInteractContentTotal(volHolder = null) {
    const extractVo = volHolder?.interactData?.commentExtractVo
      ?? ctx.interactData?.commentExtractVo
    if (!extractVo) return
    extractVo.contentTotal = Number(extractVo.contentTotal ?? 0) + 1
  }

  /**
   * 卷互动区 contentTotal 减值（删除评论/回复）
   * @param {{ interactData?: object } | null} [volHolder]
   * @param {number} [amount=1]
   */
  function decrementVolInteractContentTotal(volHolder = null, amount = 1) {
    const extractVo = volHolder?.interactData?.commentExtractVo
      ?? ctx.interactData?.commentExtractVo
    if (!extractVo) return
    const delta = Math.max(0, Number(amount) || 0)
    extractVo.contentTotal = Math.max(0, Number(extractVo.contentTotal ?? 0) - delta)
  }

  /**
   * 主评论在 contentTotal 中占用的条数（1 条评论 + 其下回复数）
   * @param {object} comment
   * @returns {number}
   */
  function countVolCommentContentUnits(comment) {
    if (!comment) return 1
    const replyCount = Math.max(
      Number(comment.replyCount ?? 0),
      comment.replies?.length ?? 0,
    )
    return 1 + replyCount
  }

  /**
   * 根据回复接口响应构建本地回复对象
   * @param {object} responseData
   * @param {string} replyContent
   * @param {object} targetItem 被回复的主评论或子回复
   * @returns {object | null}
   */
  function buildSubmittedVolReply(responseData, replyContent, targetItem) {
    const contentVo = responseData?.content
    if (contentVo && typeof contentVo === 'object') {
      return ensureCommentMessageId(
        normalizeMessageFields({
          ...contentVo,
          content: contentVo.content || replyContent,
          toUserName: contentVo.toUserName || targetItem?.userName || '',
          isBelongMe: contentVo.isBelongMe ?? true,
        }),
      )
    }

    const messageId = resolveAddMessageId(responseData)
    if (!messageId) return null

    return ensureCommentMessageId(
      normalizeMessageFields({
        messageId,
        userId: ctx.currentUser?.id || '',
        userName: ctx.currentUser?.name || '我',
        userHeadUrl: getUserAvatarFromProfile(ctx.currentUser) || getAvatarUrl(),
        content: replyContent,
        toUserName: targetItem?.userName || '',
        replyTime: formatLocalCommentTime(),
        commentTime: formatLocalCommentTime(),
        likeCount: 0,
        isMyLike: false,
        isBelongMe: true,
      }),
    )
  }

  /**
   * 将新回复追加到主评论并展开列表
   * @param {object} rootComment
   * @param {object} newReply
   */
  function appendReplyToVolComment(rootComment, newReply) {
    if (!rootComment.replies) {
      rootComment.replies = []
    }
    rootComment.replies.push(newReply)
    rootComment.replyCount = (rootComment.replyCount || 0) + 1
    rootComment.existReply = true
    rootComment.showReplies = true
  }

  async function refetchVolInteractForHolder(volHolder) {
    if (!volHolder?.volId) return
    try {
      const response = await fetchVolInteractApi(volHolder.volId, ctx.token)
      if (response.data.code === 200 || response.data.code === 0) {
        volHolder.interactData = normalizeVolInteractData(response.data.data || null)
      }
    } catch (error) {
      console.error('刷新卷互动数据失败:', error)
    }
  }

  function handleReward() {
    if (!ctx.token) {
      ctx.$emit('show-login-popup')
      return
    }
    const catalog = ctx.catalogData
    const comicTitle =
      ctx.comicName ||
      (catalog && (catalog.name || catalog.title || catalog.comicTitle)) ||
      ''
    openComicTip({
      comicId: String(ctx.comicId),
      comicTitle,
      source: 'reader',
      volId: ctx.volId ? String(ctx.volId) : undefined,
      volNum: ctx.volNum != null ? Number(ctx.volNum) : undefined,
    })
  }

  async function submitComment() {
    if (!ctx.token) {
      ctx.$emit('show-popup-message', '请您登录后操作', 'login-required')
      return
    }

    if (!ctx.commentInput.trim()) {
      ctx.$emit('show-popup-message', '请输入评论内容', 'warning')
      return
    }

    if (!ctx.volId) {
      return
    }

    const commentContent = ctx.commentInput.trim()

    try {
      const response = await addMessageApi(
        {
          belongId: ctx.volId,
          type: 'COMMENT_VOL',
          content: commentContent,
        },
        ctx.token
      )

      if (response.data.code === 200 || response.data.code === 0) {
        ctx.commentInput = ''

        if (!ctx.interactData) {
          ctx.interactData = {}
        }
        if (!ctx.interactData.commentExtractVo) {
          ctx.interactData.commentExtractVo = { commentVos: [], contentTotal: 0 }
        }

        const newComment = buildSubmittedVolComment(response.data, commentContent)
        if (!newComment?.messageId) {
          ctx.$emit('show-popup-message', '评论成功', 'success')
          await fetchInteractData()
          return
        }

        appendCommentToExtractVo(ctx.interactData.commentExtractVo, newComment)
        ctx.$emit('show-popup-message', '评论成功', 'success')
      } else {
        ctx.$emit('show-popup-message', response.data.msg || '评论失败', 'error')
      }
    } catch (error) {
      console.error('提交评论失败:', error)
      ctx.$emit('show-popup-message', '评论失败', 'error')
    }
  }

  async function handleCommentLike(comment) {
    if (!ctx.token) {
      ctx.$emit('show-popup-message', '请您登录后操作', 'login-required')
      return
    }

    const targetId = comment.messageId ?? comment.id
    if (!targetId) {
      ctx.$emit('show-popup-message', '评论信息未就绪，请稍后重试', 'error')
      return
    }

    try {
      const isLike = isVolCommentLiked(comment)
      const payload = {
        targetId: String(targetId),
        type: 'MESSAGE',
      }
      const response = isLike
        ? await cancelMessageLikeApi(payload, ctx.token)
        : await insertMessageLikeApi(payload, ctx.token)

      if (response.data.code === 200 || response.data.code === 0) {
        comment.isMyLike = !isLike
        comment.isLike = comment.isMyLike
        comment.likeCount = isLike
          ? (comment.likeCount - 1)
          : (comment.likeCount + 1)
      } else {
        ctx.$emit('show-popup-message', response.data.msg || '操作失败', 'error')
      }
    } catch (error) {
      console.error('评论点赞操作失败:', error)
      ctx.$emit('show-popup-message', '操作失败', 'error')
    }
  }

  /**
   * 加载更多卷评论（首次进入不传分页；查看更多时 pageSize=5）
   * @param {{ volId?: string|number, interactData?: object } | null} [volHolder] 已加载的其他卷；不传则当前卷
   */
  async function loadMoreComments(volHolder = null) {
    const volId = volHolder?.volId ?? ctx.volId
    const interactHolder = volHolder ?? ctx
    const extractVo = interactHolder.interactData?.commentExtractVo

    if (!volId || !extractVo) return

    const loadedCount = extractVo.commentVos?.length ?? 0
    const isCollapsed = Boolean(extractVo.isCollapsed)

    if (
      isCollapsed &&
      loadedCount > VOL_INTERACT_COLLAPSED_COMMENT_LIMIT &&
      !extractVo.hasNext
    ) {
      extractVo.isCollapsed = false
      return
    }

    if (!extractVo.hasNext) return
    if (ctx.isLoadingMoreComments) return

    extractVo.isCollapsed = false

    const nextPageNum = Number(extractVo.pageNum ?? 1) + 1

    try {
      ctx.isLoadingMoreComments = true
      const response = await fetchVolInteractApi(volId, ctx.token, {
        pageNum: nextPageNum,
        pageSize: VOL_INTERACT_LOAD_MORE_PAGE_SIZE,
      })

      if (response.data.code === 200 || response.data.code === 0) {
        interactHolder.interactData = mergeVolInteractComments(
          interactHolder.interactData,
          response.data.data,
        )
      } else {
        ctx.$emit('show-popup-message', response.data.msg || '加载评论失败', 'error')
      }
    } catch (error) {
      console.error('加载更多评论失败:', error)
      ctx.$emit('show-popup-message', '加载评论失败', 'error')
    } finally {
      ctx.isLoadingMoreComments = false
    }
  }

  /**
   * 收起评论，仅保留前 5 条展示
   * @param {{ interactData?: object } | null} [volHolder]
   */
  function collapseComments(volHolder = null) {
    const interactHolder = volHolder ?? ctx
    const extractVo = interactHolder.interactData?.commentExtractVo
    const loadedCount = extractVo?.commentVos?.length ?? 0
    if (!extractVo || loadedCount <= VOL_INTERACT_COLLAPSED_COMMENT_LIMIT) return
    extractVo.isCollapsed = true
  }

  function getAvatarUrl() {
    const avatar = getUserAvatarFromProfile(ctx.currentUser)
    if (avatar) {
      return resolveAvatarUrl(avatar)
    }
    return PLACEHOLDER_ASSETS.defaultAvatar
  }

  function getCommentAvatarUrl(comment) {
    const avatar = comment.userHeadUrl || comment.avatar || comment.userAvatar
    if (avatar) {
      return resolveAvatarUrl(avatar)
    }
    return PLACEHOLDER_ASSETS.defaultAvatar
  }

  function handleAvatarError(event) {
    // 防止死循环：如果已经是默认头像或已经处理过，就不再处理
    const currentSrc = event.target.src || ''
    if (isDefaultAvatarSrc(currentSrc) || event.target.dataset.errorHandled === 'true') {
      // 如果默认头像也加载失败，隐藏图片
      event.target.style.display = 'none'
      return
    }
    // 标记已处理，防止重复触发
    event.target.dataset.errorHandled = 'true'
    event.target.src = PLACEHOLDER_ASSETS.defaultAvatar
  }

  function handleCommentAvatarError(event) {
    // 防止死循环：如果已经是默认头像或已经处理过，就不再处理
    const currentSrc = event.target.src || ''
    if (isDefaultAvatarSrc(currentSrc) || event.target.dataset.errorHandled === 'true') {
      // 如果默认头像也加载失败，隐藏图片
      event.target.style.display = 'none'
      return
    }
    // 标记已处理，防止重复触发
    event.target.dataset.errorHandled = 'true'
    event.target.src = PLACEHOLDER_ASSETS.defaultAvatar
  }

  function formatCommentTime(dateTimeString) {
    if (!dateTimeString) return ''
    // 格式：2026-01-02 00:47:01 -> 2026-1-2
    const date = new Date(dateTimeString)
    if (isNaN(date.getTime())) {
      // 如果无法解析，尝试直接处理字符串
      const datePart = dateTimeString.split(' ')[0]
      const parts = datePart.split('-')
      if (parts.length === 3) {
        return `${parts[0]}-${parseInt(parts[1])}-${parseInt(parts[2])}`
      }
      return datePart
    }
    return `${date.getFullYear()}-${date.getMonth() + 1}-${date.getDate()}`
  }

  async function handleLoadedVolumeLike(vol) {
    if (!ctx.token) {
      ctx.$emit('show-popup-message', '请您登录后操作', 'login-required')
      return
    }

    if (!vol.interactData || !vol.volId) {
      return
    }

    try {
      const isLike = vol.interactData.isLike
      const payload = {
        targetId: vol.volId,
        type: 'VOL',
      }
      const response = isLike
        ? await cancelMessageLikeApi(payload, ctx.token)
        : await insertMessageLikeApi(payload, ctx.token)

      if (response.data.code === 200 || response.data.code === 0) {
        // 更新本地状态
        vol.interactData.isLike = !isLike
        vol.interactData.likeCount = isLike 
          ? (vol.interactData.likeCount - 1) 
          : (vol.interactData.likeCount + 1)
      } else {
        ctx.$emit('show-popup-message', response.data.msg || '操作失败', 'error')
      }
    } catch (error) {
      console.error('已完成的卷点赞操作失败:', error)
      ctx.$emit('show-popup-message', '操作失败', 'error')
    }
  }

  async function handleLoadedVolumeCollect(vol) {
    if (!ctx.token) {
      ctx.$emit('show-popup-message', '请您登录后操作', 'login-required')
      return
    }

    if (!vol.interactData || !vol.volId) {
      return
    }

    try {
      const isCollect = vol.interactData.isCollect
      const collectPayload = {
        comicId: ctx.comicId,
        volNum: vol.volNum,
      }
      const response = isCollect
        ? await cancelVolCollectApi(collectPayload, ctx.token)
        : await insertVolCollectApi(collectPayload, ctx.token)

      if (response.data.code === 200 || response.data.code === 0) {
        // 更新本地状态
        vol.interactData.isCollect = !isCollect
      } else {
        ctx.$emit('show-popup-message', response.data.msg || '操作失败', 'error')
      }
    } catch (error) {
      console.error('已完成的卷收藏操作失败:', error)
      ctx.$emit('show-popup-message', '操作失败', 'error')
    }
  }

  async function submitLoadedVolumeComment(vol) {
    if (!ctx.token) {
      ctx.$emit('show-popup-message', '请您登录后操作', 'login-required')
      return
    }

    if (!vol.commentInput.trim()) {
      ctx.$emit('show-popup-message', '请输入评论内容', 'warning')
      return
    }

    if (!vol.volId) {
      return
    }

    const commentContent = vol.commentInput.trim()

    try {
      const response = await addMessageApi(
        {
          belongId: vol.volId,
          type: 'COMMENT_VOL',
          content: commentContent,
        },
        ctx.token
      )

      if (response.data.code === 200 || response.data.code === 0) {
        vol.commentInput = ''

        if (!vol.interactData) {
          vol.interactData = {}
        }
        if (!vol.interactData.commentExtractVo) {
          vol.interactData.commentExtractVo = { commentVos: [], contentTotal: 0 }
        }

        const newComment = buildSubmittedVolComment(response.data, commentContent)
        if (!newComment?.messageId) {
          ctx.$emit('show-popup-message', '评论成功', 'success')
          await refetchVolInteractForHolder(vol)
          return
        }

        appendCommentToExtractVo(vol.interactData.commentExtractVo, newComment)
        ctx.$emit('show-popup-message', '评论成功', 'success')
      } else {
        ctx.$emit('show-popup-message', response.data.msg || '评论失败', 'error')
      }
    } catch (error) {
      console.error('提交已完成的卷评论失败:', error)
      ctx.$emit('show-popup-message', '评论失败', 'error')
    }
  }

  const VOL_COMMENT_REPLY_TYPE = 'COMMENT_VOL'

  /**
   * 解析卷互动上下文（当前卷或已加载的其他卷）
   * @param {{ volId?: string|number, interactData?: object } | null} [volHolder]
   * @returns {{ volId: string|number|null, getCommentVos: () => object[] }}
   */
  function resolveVolInteractContext(volHolder = null) {
    if (volHolder) {
      return {
        volId: volHolder.volId ?? null,
        getCommentVos: () => volHolder.interactData?.commentExtractVo?.commentVos || [],
      }
    }
    return {
      volId: ctx.volId ?? null,
      getCommentVos: () => ctx.interactData?.commentExtractVo?.commentVos || [],
    }
  }

  /**
   * 切换某条评论/回复的回复输入框
   * @param {string|number} messageId
   */
  function toggleReplyBox(messageId) {
    ctx.showReplyBoxMap[messageId] = !ctx.showReplyBoxMap[messageId]
    if (!ctx.showReplyBoxMap[messageId]) {
      ctx.replyContentMap[messageId] = ''
    }
  }

  /**
   * 切换是否展开某条主评论的全部回复
   * @param {string|number} messageId
   */
  function toggleShowAllReplies(messageId) {
    ctx.showAllRepliesMap[messageId] = !ctx.showAllRepliesMap[messageId]
  }

  /**
   * 查找回复所属的主评论
   * @param {object} targetItem
   * @param {{ volId?: string|number, interactData?: object } | null} [volHolder]
   * @returns {object | null}
   */
  function findRootVolComment(targetItem, volHolder = null) {
    if (!targetItem) return null

    const { getCommentVos } = resolveVolInteractContext(volHolder)
    const commentVos = getCommentVos()
    const targetId = String(targetItem.messageId ?? targetItem.id ?? '')
    if (!targetId) return null

    const isMainComment = commentVos.some(
      (c) => String(c.messageId ?? c.id ?? '') === targetId,
    )
    if (isMainComment) {
      return targetItem
    }

    return (
      commentVos.find((c) =>
        (c.replies || []).some(
          (r) => String(r.messageId ?? r.id ?? '') === targetId,
        ),
      ) || targetItem
    )
  }

  /**
   * 加载卷评论的回复列表（首屏）
   * @param {object} comment
   * @param {{ volId?: string|number, interactData?: object } | null} [volHolder]
   */
  async function loadVolCommentReplies(comment, volHolder = null) {
    const messageId = comment.messageId
    if (!messageId || ctx.loadingRepliesMap[messageId]) return

    const { volId } = resolveVolInteractContext(volHolder)
    if (!volId) return

    try {
      ctx.loadingRepliesMap[messageId] = true
      const response = await fetchMessageReplyPageApi(
        {
          messageId: String(messageId),
          type: VOL_COMMENT_REPLY_TYPE,
          pageNum: 1,
          pageSize: ctx.replyPageSize,
        },
        ctx.token,
      )

      if (response.data.code === 200 || response.data.code === 0) {
        comment.replies = (response.data.contents || []).map((r) =>
          ensureCommentMessageId(r),
        )
        comment.existReply = comment.replies.length > 0
        comment.replyCount = Number(response.data.total) || comment.replies.length
      }
    } catch (error) {
      console.error('请求卷评论回复错误:', error)
    } finally {
      ctx.loadingRepliesMap[messageId] = false
    }
  }

  /**
   * 展开/收起卷评论回复列表
   * @param {object} comment
   * @param {{ volId?: string|number, interactData?: object } | null} [volHolder]
   */
  async function toggleVolCommentReplies(comment, volHolder = null) {
    if (comment.showReplies) {
      comment.showReplies = false
      ctx.showAllRepliesMap[comment.messageId] = false
      return
    }

    if (!comment.replies?.length) {
      ctx.pendingExpandRepliesMap[comment.messageId] = true
      try {
        await loadVolCommentReplies(comment, volHolder)
      } finally {
        ctx.pendingExpandRepliesMap[comment.messageId] = false
      }
    }
    comment.showReplies = true
  }

  /**
   * 提交卷评论回复
   * @param {object} targetItem 主评论或子回复
   * @param {{ volId?: string|number, interactData?: object } | null} [volHolder]
   */
  async function submitVolCommentReply(targetItem, volHolder = null) {
    if (!ctx.token) {
      ctx.$emit('show-popup-message', '请您登录后操作', 'login-required')
      return
    }

    const messageId = targetItem.messageId
    const replyContent = ctx.replyContentMap[messageId] || ''
    if (!replyContent.trim()) {
      ctx.$emit('show-popup-message', '请输入回复内容', 'warning')
      return
    }

    const { volId } = resolveVolInteractContext(volHolder)
    if (!volId) return
    if (ctx.submittingReplyMap[messageId]) return

    const rootComment = findRootVolComment(targetItem, volHolder)
    const rootCommentId = rootComment?.messageId ?? messageId

    try {
      ctx.submittingReplyMap[messageId] = true
      const response = await replyMessageApi(
        {
          belongId: volId,
          content: replyContent.trim(),
          parentId: messageId,
          type: VOL_COMMENT_REPLY_TYPE,
        },
        ctx.token,
      )

      if (response.data.code === 200 || response.data.code === 0) {
        const trimmedContent = replyContent.trim()
        ctx.replyContentMap[messageId] = ''
        ctx.showReplyBoxMap[messageId] = false

        if (rootComment) {
          const newReply = buildSubmittedVolReply(
            response.data,
            trimmedContent,
            targetItem,
          )
          if (newReply?.messageId) {
            appendReplyToVolComment(rootComment, newReply)
            incrementVolInteractContentTotal(volHolder)
          } else {
            ctx.pendingExpandRepliesMap[rootCommentId] = true
            try {
              await loadVolCommentReplies(rootComment, volHolder)
              rootComment.existReply = (rootComment.replies?.length ?? 0) > 0
              rootComment.showReplies = true
              incrementVolInteractContentTotal(volHolder)
            } finally {
              ctx.pendingExpandRepliesMap[rootCommentId] = false
            }
          }
        }

        ctx.$emit('show-popup-message', response.data.msg || '回复成功', 'success')
      } else {
        ctx.$emit('show-popup-message', response.data.msg || '发布回复失败', 'error')
      }
    } catch (error) {
      console.error('提交卷评论回复失败:', error)
      ctx.$emit('show-popup-message', '发布回复失败', 'error')
    } finally {
      ctx.submittingReplyMap[messageId] = false
    }
  }

  /**
   * 主评论是否可能有回复（用于展示展开入口）
   * @param {object} comment
   * @returns {boolean}
   */
  function volCommentHasReplies(comment) {
    if (!comment) return false
    return (comment.replyCount || 0) > 0 || Boolean(comment.existReply)
  }

  /**
   * 从卷互动评论列表中移除一条主评论并更新计数
   * @param {object} extractVo
   * @param {object} comment
   */
  function removeCommentFromExtractVo(extractVo, comment) {
    const targetId = String(comment.messageId ?? comment.id ?? '')
    if (!extractVo || !targetId) return

    const contentUnits = countVolCommentContentUnits(comment)

    extractVo.commentVos = (extractVo.commentVos || []).filter(
      (item) => String(item.messageId ?? item.id ?? '') !== targetId,
    )
    extractVo.contentTotal = Math.max(0, Number(extractVo.contentTotal ?? 0) - contentUnits)
    extractVo.totalCount = Math.max(0, Number(extractVo.totalCount ?? 0) - 1)
  }

  /**
   * 删除卷内容主评论
   * @param {object} comment
   * @param {{ volId?: string|number, interactData?: object } | null} [volHolder]
   */
  async function deleteVolComment(comment, volHolder = null) {
    if (!ctx.token) {
      ctx.$emit('show-popup-message', '请您登录后操作', 'login-required')
      return
    }

    const messageId = comment.messageId ?? comment.id
    if (!messageId) return

    try {
      const response = await deleteMessageApi(String(messageId), ctx.token)
      if (response.data.code !== 0 && response.data.code !== 200) {
        ctx.$emit('show-popup-message', response.data.msg || '删除失败', 'error')
        return
      }

      const extractVo = volHolder?.interactData?.commentExtractVo
        ?? ctx.interactData?.commentExtractVo
      if (extractVo) {
        removeCommentFromExtractVo(extractVo, comment)
      } else if (volHolder?.volId) {
        await refetchVolInteractForHolder(volHolder)
      } else {
        await fetchInteractData()
      }

      ctx.$emit('show-popup-message', '删除成功', 'success')
    } catch (error) {
      console.error('删除卷评论失败:', error)
      ctx.$emit('show-popup-message', error.response?.data?.msg || '删除失败', 'error')
    }
  }

  /**
   * 删除卷内容评论回复
   * @param {object} comment 主评论
   * @param {object} reply
   * @param {{ volId?: string|number, interactData?: object } | null} [volHolder]
   */
  async function deleteVolCommentReply(comment, reply, volHolder = null) {
    if (!ctx.token) {
      ctx.$emit('show-popup-message', '请您登录后操作', 'login-required')
      return
    }

    const replyId = reply.messageId ?? reply.id
    if (!replyId) return

    try {
      const response = await deleteMessageApi(String(replyId), ctx.token)
      if (response.data.code !== 0 && response.data.code !== 200) {
        ctx.$emit('show-popup-message', response.data.msg || '删除失败', 'error')
        return
      }

      await loadVolCommentReplies(comment, volHolder)
      if (!comment.replies?.length) {
        comment.existReply = false
        comment.showReplies = false
        comment.replyCount = 0
      } else {
        comment.replyCount = comment.replies.length
      }
      comment.showReplies = Boolean(comment.replies?.length)
      decrementVolInteractContentTotal(volHolder, 1)

      ctx.$emit('show-popup-message', '删除成功', 'success')
    } catch (error) {
      console.error('删除卷评论回复失败:', error)
      ctx.$emit('show-popup-message', error.response?.data?.msg || '删除失败', 'error')
    }
  }

  async function handleLoadedVolumeCommentLike(vol, comment) {
    if (!ctx.token) {
      ctx.$emit('show-popup-message', '请您登录后操作', 'login-required')
      return
    }

    const targetId = comment.messageId ?? comment.id
    if (!targetId) {
      ctx.$emit('show-popup-message', '评论信息未就绪，请稍后重试', 'error')
      return
    }

    try {
      const isLike = isVolCommentLiked(comment)
      const payload = {
        targetId: String(targetId),
        type: 'MESSAGE',
      }
      const response = isLike
        ? await cancelMessageLikeApi(payload, ctx.token)
        : await insertMessageLikeApi(payload, ctx.token)

      if (response.data.code === 200 || response.data.code === 0) {
        comment.isMyLike = !isLike
        comment.isLike = comment.isMyLike
        comment.likeCount = isLike
          ? (comment.likeCount - 1)
          : (comment.likeCount + 1)
      } else {
        ctx.$emit('show-popup-message', response.data.msg || '操作失败', 'error')
      }
    } catch (error) {
      console.error('已加载卷评论点赞操作失败:', error)
      ctx.$emit('show-popup-message', '操作失败', 'error')
    }
  }

  Object.assign(bridge, {
    readerCommentElId,
    tryScrollToHighlightedVolComment,
    fetchInteractData,
    checkCurrentVolumeComplete,
    fetchCurrentUser,
    handleLike,
    handleCollect,
    handleReward,
    submitComment,
    handleCommentLike,
    loadMoreComments,
    collapseComments,
    getAvatarUrl,
    getCommentAvatarUrl,
    handleAvatarError,
    handleCommentAvatarError,
    formatCommentTime,
    handleLoadedVolumeLike,
    handleLoadedVolumeCollect,
    submitLoadedVolumeComment,
    handleLoadedVolumeCommentLike,
    toggleReplyBox,
    toggleShowAllReplies,
    toggleVolCommentReplies,
    submitVolCommentReply,
    volCommentHasReplies,
    deleteVolComment,
    deleteVolCommentReply,
  })
}
